import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Download, RefreshCw, Upload, Users, Wand2 } from "lucide-react";

/**
 * Admin page: bulk-import legacy lifetime members from a CSV of emails.
 * Every imported email can then claim a `free_forever` account on first
 * login (see routes/auth.py legacy-claim helpers).
 *
 * Endpoints used:
 *   POST /api/admin/legacy-lifetime/import   { csv_url }
 *   GET  /api/admin/legacy-lifetime/stats
 */
export default function AdminLegacyImports() {
  const [csvUrl, setCsvUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const refreshStats = async () => {
    setLoadingStats(true);
    try {
      const { data } = await api.get("/admin/legacy-lifetime/stats");
      setStats(data);
    } catch (e) {
      toast.error("تعذّر تحميل الإحصائيات");
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => { refreshStats(); }, []);

  const runImport = async () => {
    if (!csvUrl.trim()) {
      toast.error("أدخل رابط CSV صالح");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/legacy-lifetime/import", {
        csv_url: csvUrl.trim(),
      });
      toast.success(
        `تم الاستيراد — أُضيف ${data.newly_inserted} · متخطى (مكرر) ${data.already_present_skipped} · الإجمالي ${data.total_rows_now}`,
        { duration: 6000 },
      );
      await refreshStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الاستيراد");
    } finally {
      setBusy(false);
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const runBackfill = async () => {
    if (!window.confirm("سيتم منح جميع أعضاء الوصول الكامل (مدى الحياة / مجاني للأبد / المشتركين النشطين) وصولاً إلى كل الكورسات المنشورة. متابعة؟")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/legacy-lifetime/backfill-all-access");
      toast.success(
        `تم المزامنة — ${data.users_processed} عضو · أُضيفت ${data.enrollments_created} تسجيلات · ${data.published_courses} كورس منشور`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التنفيذ");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" dir="rtl" data-testid="admin-legacy-imports">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-semibold flex items-center gap-2">
          <Users className="h-6 w-6 text-primary" />
          الأعضاء القدامى — استيراد
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          استورد قائمة بريدية من طلاب دورة قديمة. أي عضو من القائمة يقدر يدخل
          التطبيق بأي كلمة سر (أول محاولة تُنشئ حسابه تلقائياً بوصول مجاني للأبد).
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="الإجمالي المستورد" value={stats?.total_imported} loading={loadingStats} />
        <StatCard label="تم الاسترداد" value={stats?.claimed} loading={loadingStats} accent />
        <StatCard label="لم يُسترَدّ بعد" value={stats?.unclaimed} loading={loadingStats} />
      </div>

      {/* Import section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" />
            استيراد من ملف CSV
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="csv-url" className="text-xs">
              رابط CSV عام (Public URL) — عمود واحد بعنوان "Email"
            </Label>
            <Input
              id="csv-url"
              type="url"
              placeholder="https://.../students.csv"
              value={csvUrl}
              onChange={(e) => setCsvUrl(e.target.value)}
              className="text-left"
              dir="ltr"
              data-testid="csv-url-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={runImport}
              disabled={busy || !csvUrl.trim()}
              className="gap-2"
              data-testid="btn-import-csv"
            >
              {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {busy ? "جاري الاستيراد..." : "ابدأ الاستيراد"}
            </Button>
            <Button
              variant="outline"
              onClick={refreshStats}
              disabled={loadingStats}
              className="gap-2"
              data-testid="btn-refresh-stats"
            >
              <RefreshCw className={`h-4 w-4 ${loadingStats ? "animate-spin" : ""}`} />
              تحديث
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            الاستيراد آمن للتكرار — الإيميلات المكررة تُتَجاهل تلقائياً.
          </p>
        </CardContent>
      </Card>

      {/* Backfill enrollments for all-access members */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            مزامنة كورسات "مدى الحياة" و"مجاني للأبد"
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            يمنح كل عضو وصول كامل (Lifetime / Free Forever / المشتركين النشطين) وصولاً إلى جميع الكورسات
            المنشورة حالياً. آمن للتكرار — الكورسات الموجودة مسبقاً لا تُكرَّر.
            <br />
            استخدمه بعد إضافة كورس جديد أو إذا وجدت أعضاء يظهر لهم عدد كورسات أقل من الفعلي.
          </p>
          <Button
            onClick={runBackfill}
            disabled={backfilling}
            className="gap-2"
            data-testid="btn-backfill-enrollments"
          >
            {backfilling ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {backfilling ? "جاري المزامنة..." : "مزامنة الكورسات الآن"}
          </Button>
        </CardContent>
      </Card>

      {/* Recent claims */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">آخر عمليات الاسترداد</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingStats ? (
            <p className="text-sm text-muted-foreground">جاري التحميل…</p>
          ) : !stats?.recent_claims?.length ? (
            <p className="text-sm text-muted-foreground">لا توجد عمليات استرداد بعد.</p>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full text-xs" data-testid="claims-table">
                <thead className="text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-start py-2 font-medium">البريد</th>
                    <th className="text-start py-2 font-medium hidden sm:table-cell">الطريقة</th>
                    <th className="text-start py-2 font-medium hidden md:table-cell">IP</th>
                    <th className="text-start py-2 font-medium">التاريخ</th>
                    <th className="text-center py-2 font-medium">كورسات</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_claims.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-secondary/30">
                      <td className="py-2 pe-2 truncate max-w-[180px]">{c.email}</td>
                      <td className="py-2 pe-2 hidden sm:table-cell">
                        <Badge variant="secondary" className="text-[10px]">{c.claimed_via || "—"}</Badge>
                      </td>
                      <td className="py-2 pe-2 hidden md:table-cell text-muted-foreground">
                        {c.ip || "—"}
                      </td>
                      <td className="py-2 pe-2 text-muted-foreground">
                        {formatDateAr(c.claimed_at)}
                      </td>
                      <td className="py-2 text-center">{c.courses_granted ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, loading, accent }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className="text-2xl font-display font-semibold mt-1"
        style={accent ? { color: "#FF6A00" } : undefined}
      >
        {loading ? "…" : (value ?? 0).toLocaleString("en-US")}
      </div>
    </Card>
  );
}

function formatDateAr(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("ar-SA", {
      year: "2-digit", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch { return "—"; }
}
